/*
 * Orders derivations — canon §7.1 (order card) + §7.2 (seats), transcribed
 * from the App-shell canon and its DC source (App Shell.dc.html ordersView /
 * seat()). Pure functions over the SHIPPED domain types (types.ts Ticket with
 * ruling-B seats[]) — the canon [demo] dataset (src/data/seed.ts) exercises
 * them ONLY in tests (ruling A).
 *
 * SOURCE OF TRUTH: the my-api holder is authoritative for allocation state
 * (PRD-ONEPULSE-PARTICIPANT-CUSTOMER-FLOW-V1; fix e75108d). applyAllocations
 * maps holders onto the ruling-B seats[]:
 *   no holder → unassigned · invited → pending · claimed/ready/consumed →
 *   assigned (mine when the allocated email is the signed-in account's).
 *
 * STATE-COLOUR SEMANTICS (canon §7.1/§7.2 [token], boardroom ruling): amber
 * `--warn` = seat(s) unassigned / needs action; blue `--info-invited`
 * (#8ab4f8) = invite pending; teal `--accent` = done/assigned. Never collapse
 * mixed per-seat states into one order-level status (§7.2 must-not).
 */
import type { OrderAllocations } from '../../data/myApi'
import type { Seat, Ticket } from '../../data/types'

// ---------------------------------------------------------------------------
// Allocation merge (live my-api holders → ruling-B seats[])
// ---------------------------------------------------------------------------

/** Merge my-api allocation holders into a ticket's seats[].
 *
 *  Per-seat targeting (BOARDROOM-2026-08-17-210222): a Team-of-N box-office
 *  entry is backed by N REAL tickets rows in my-api (getOrderAllocations
 *  returns one row per row). The entry's rows are resolved by ticketUnitId
 *  (the row matching the box-office id anchors the unit; a pre-deploy my-api
 *  without the field falls back to typeName grouping with a consumption
 *  cursor so two same-type units never share rows). Each my-api row becomes
 *  ONE Seat carrying that row's OWN ticketId + holder state — the old model
 *  pinned the single holder to seat 1 and hardcoded seats ≠ 1 unassigned,
 *  which made every seat's assign submit target seat 1's ticket (the prod
 *  62970f6a 11-holders/10-revoked incident).
 *
 *  PER-ROW WIRE (CMT-2026-08-29-027): when box-office emits one entry PER
 *  ROW (several same-type entries anchoring into ONE unit — the live stage
 *  shape), each row is dealt to exactly one entry (own row first), so a
 *  holder can never render on two entries and an edit on one displayed
 *  ticket can never target a row shown on another.
 *
 *  Degraded honesty: when my-api returns FEWER rows than personsPerTicket the
 *  missing places render as unassigned seats WITHOUT a ticketId — the screens
 *  show them non-assignable with honest copy; nothing is fabricated. */
export function applyAllocations(
  tickets: Ticket[],
  allocations: OrderAllocations | null,
  sessionEmail: string | undefined,
): Ticket[] {
  if (!allocations) {
    // my-api not readable — allocation state is UNKNOWN. Nothing is invented:
    // seats render unassigned with actions disabled behind the honest service
    // notice (never box-office seatStatus, which is not allocation-authoritative).
    return tickets.map((t) => ({
      ...t,
      seats: t.seats.map((s) => ({ seatNo: s.seatNo, status: 'unassigned' as const })),
    }))
  }
  const me = (sessionEmail ?? '').trim().toLowerCase()
  const rows = allocations.tickets
  // typeName-fallback cursor (pre-ticketUnitId my-api only): same-type rows are
  // dealt sequentially across the order's team entries so two same-type units
  // in one order never duplicate targets. Row order is my-api's stable
  // created_at, ticket_id ordering.
  const consumedByType = new Map<string, number>()
  // CROSS-TICKET FAN-OUT FIX (CMT-2026-08-29-027, dispatch BOARDROOM-2026-08-
  // 29-114614; Duncan live walk, order T-ORD-7EUR95): the LIVE box-office wire
  // emits ONE entry PER SEAT ROW — a Doubles unit's 2 rows arrive as TWO
  // same-type entries whose ids are the unit's own row ids (stage-probed
  // 2026-08-29 on ORD-B4AJ3G / ORD-EG4YXT). Filtering the WHOLE unit under
  // every anchoring entry therefore rendered the same rows on both "tickets":
  // one allocate showed up as Person 2 on BOTH, and both tickets ended with
  // identical people (a display-side collision — the server write is strictly
  // ticket_id-scoped). Rule: each my-api row renders under EXACTLY ONE entry.
  // When several entries anchor into the SAME unit (the per-row wire), each
  // entry keeps its OWN row plus a once-only sequential deal of any unit rows
  // not exposed as entries; a unit with a single anchoring entry keeps the
  // original entry-per-unit behaviour (BOARDROOM-2026-08-17-210222) unchanged.
  const entryIds = new Set(tickets.map((x) => x.id))
  const anchoringEntriesPerUnit = new Map<string, number>()
  for (const x of tickets) {
    if (x.seats.length <= 1) continue
    const a = rows.find((r) => r.ticketId === x.id)
    if (a?.ticketUnitId) {
      anchoringEntriesPerUnit.set(a.ticketUnitId, (anchoringEntriesPerUnit.get(a.ticketUnitId) ?? 0) + 1)
    }
  }
  const dealtRowIds = new Set<string>()
  // Team-purchase stamps (BOARDROOM-2026-09-09-161918): per-unit member
  // counter so per-row-wire siblings number 1..N in entry order (box-office
  // order = purchase order; my-api rows are created_at,ticket_id stable).
  const memberSeqByUnit = new Map<string, number>()
  return tickets.map((t) => {
    const anchor = rows.find((x) => x.ticketId === t.id) ?? null
    // Per-row-wire entries render exactly their dealt rows — no padding: the
    // unit's other places live on the sibling entries, so a phantom padded
    // place would double-count the purchase.
    let sharedUnit = false
    let unitRows: typeof rows
    if (t.seats.length <= 1) {
      // Solo ticket: exactly its own row (behaviour unchanged + ticketId stamp).
      unitRows = anchor ? [anchor] : []
    } else if (anchor?.ticketUnitId && (anchoringEntriesPerUnit.get(anchor.ticketUnitId) ?? 1) > 1) {
      sharedUnit = true
      const leftovers = rows.filter(
        (x) =>
          x.ticketUnitId === anchor.ticketUnitId &&
          x.ticketId !== anchor.ticketId &&
          !entryIds.has(x.ticketId) &&
          !dealtRowIds.has(x.ticketId),
      )
      unitRows = [anchor, ...leftovers.slice(0, Math.max(0, t.seats.length - 1))]
      for (const r of unitRows) dealtRowIds.add(r.ticketId)
    } else if (anchor?.ticketUnitId) {
      unitRows = rows.filter((x) => x.ticketUnitId === anchor.ticketUnitId)
    } else if (anchor) {
      const typeRows = rows.filter((x) => x.typeName === anchor.typeName)
      const start = consumedByType.get(anchor.typeName) ?? 0
      unitRows = typeRows.slice(start, start + t.seats.length)
      consumedByType.set(anchor.typeName, start + unitRows.length)
    } else {
      // Anchor missing from my-api entirely — allocation state for this entry
      // is unknown; every place renders unassigned without a target.
      unitRows = []
    }
    // One Seat per my-api row (my-api is allocation-authoritative for the real
    // row count); pad up to personsPerTicket with honest target-less places —
    // EXCEPT on a shared (per-row-wire) unit, where the remaining places are
    // real rows rendered on the sibling entries, not missing seats.
    const n = sharedUnit ? unitRows.length : Math.max(t.seats.length, unitRows.length)
    const seats: Seat[] = []
    for (let i = 0; i < n; i++) {
      const row = unitRows[i]
      const seatNo = i + 1
      if (!row) {
        seats.push({ seatNo, status: 'unassigned' })
        continue
      }
      const holder = row.holder && row.holder.state !== 'revoked' ? row.holder : null
      if (!holder) {
        seats.push({ seatNo, status: 'unassigned', ticketId: row.ticketId })
        continue
      }
      const email = holder.allocatedEmail ?? undefined
      const mine = !!me && !!email && email.trim().toLowerCase() === me
      seats.push(
        holder.state === 'invited'
          ? { seatNo, status: 'pending', ticketId: row.ticketId, holder: { email } }
          : { seatNo, status: 'assigned', ticketId: row.ticketId, holder: { email }, mine },
      )
    }
    // Team-purchase stamps (BOARDROOM-2026-09-09-161918): rider fields the
    // Orders card reads for per-team colour + "Ticket {n}" labels. Only
    // unit-resolved entries are stamped — the typeName fallback and solo
    // entries stay untouched (colour stays type-keyed there, no suffix).
    if (sharedUnit && anchor?.ticketUnitId) {
      const seq = (memberSeqByUnit.get(anchor.ticketUnitId) ?? 0) + 1
      memberSeqByUnit.set(anchor.ticketUnitId, seq)
      return {
        ...t,
        seats,
        teamUnitId: anchor.ticketUnitId,
        teamMemberSeq: seq,
        teamMemberCount: anchoringEntriesPerUnit.get(anchor.ticketUnitId),
      }
    }
    if (anchor?.ticketUnitId && t.seats.length > 1) {
      // Whole unit in ONE entry: team identity for colour, but the places are
      // numbered inside the block — no member suffix.
      return { ...t, seats, teamUnitId: anchor.ticketUnitId }
    }
    return { ...t, seats }
  })
}

/** A seat that CANNOT be assigned right now: my-api resolved this unit (a
 *  sibling seat carries a real ticket row) but returned no row for this place
 *  (under-materialized unit). No target ⇒ no assign action — honest copy, no
 *  fabrication (BOARDROOM-2026-08-17-210222). Fixture/degraded-service seats
 *  (NO seat in the ticket has a ticketId) are unaffected: their actions are
 *  already gated by the alloc-state notices. */
export function seatUnassignable(t: Ticket, s: Seat): boolean {
  return !s.ticketId && t.seats.some((x) => !!x.ticketId)
}

// ---------------------------------------------------------------------------
// Per-ticket derivations (canon §7.1 ticket row / §7.2 status pill)
// ---------------------------------------------------------------------------

/** Display bucket for a seat: checked_in is terminal-assigned (per-ticket
 *  state machine Unassigned → Invited·pending → Assigned → Checked-in). */
const seatFilled = (s: Seat) => s.status !== 'unassigned'

/** Holder display line: name, else email (never fabricated). */
export function holderLabel(s: Seat): string {
  return s.holder?.name || s.holder?.email || ''
}

export type TicketDotState = 'need' | 'pending' | 'done'

export interface TicketAssignment {
  assigned: number
  seats: number
  done: boolean
  pending: boolean
  mine: boolean
  /** Dot / accent state: amber need · blue pending · teal done (§7.1 [token]). */
  dot: TicketDotState
  /** Sub-line per the canon templates (§7.1): needs → `{n} to assign` /
   *  `{n} of {m} still to assign`; done → `You` / holder / `{n} people
   *  assigned`; pending → `Waiting for them to accept`. */
  sub: string
  /** Tick glyph when done: `✓` (pending `◔`) (§7.1). */
  tickGlyph: string
}

export function ticketAssignment(t: Ticket): TicketAssignment {
  const assigned = t.seats.filter(seatFilled).length
  const need = t.seats.length - assigned
  const done = need === 0
  const pending = t.seats.some((s) => s.status === 'pending')
  const mine = t.seats.every((s) => !!s.mine)
  const names = t.seats.filter(seatFilled).map(holderLabel)
  const sub = done
    ? pending
      ? 'Waiting for them to accept'
      : mine
        ? 'You'
        : names.length > 1
          ? `${names.length} places assigned`
          : names[0] ?? ''
    : need === t.seats.length
      ? `${need} to assign`
      : `${need} of ${t.seats.length} still to assign`
  return {
    assigned,
    seats: t.seats.length,
    done,
    pending,
    mine,
    dot: !done ? 'need' : pending ? 'pending' : 'done',
    sub,
    tickGlyph: pending ? '◔' : '✓',
  }
}

/** §7.2 header status pill: needs → `{k} of {n} assigned` (amber) / pending →
 *  `Invite pending` (blue) / mine → `✓ Your ticket` / done → `✓ Complete`. */
export function ticketStatusPill(t: Ticket): { text: string; dot: TicketDotState } {
  const a = ticketAssignment(t)
  const text = !a.done
    ? `${a.assigned} of ${a.seats} assigned`
    : a.pending
      ? 'Invite pending'
      : a.mine
        ? '✓ Your ticket'
        : '✓ Complete'
  return { text, dot: a.dot }
}

/** §7.2 mono suffix `· {n} places` / `· 1 place` (canon PLACE, R7 "2 places included"). */
export function peopleLabel(t: Ticket): string {
  return t.seats.length > 1 ? `${t.seats.length} places` : '1 place'
}

// ---------------------------------------------------------------------------
// Order summary (canon §7.1: demo ⇒ `6 tickets · 3 need action · 3 complete ·
// 4 of 8 people assigned`, bar 50%)
// ---------------------------------------------------------------------------

export interface OrderSummary {
  ticketCount: number
  needTickets: number
  doneTickets: number
  assigned: number
  total: number
  pct: number
}

export function orderSummary(tickets: Ticket[]): OrderSummary {
  const seats = tickets.flatMap((t) => t.seats)
  const total = seats.length
  const assigned = seats.filter(seatFilled).length
  const needTickets = tickets.filter((t) => t.seats.some((s) => s.status === 'unassigned')).length
  const doneTickets = tickets.length - needTickets
  return {
    ticketCount: tickets.length,
    needTickets,
    doneTickets,
    assigned,
    total,
    pct: total === 0 ? 0 : Math.round((assigned / total) * 100),
  }
}

// ---------------------------------------------------------------------------
// Seat block (canon §7.2)
// ---------------------------------------------------------------------------

export interface SeatView {
  seatNo: number
  filled: boolean
  /** Roundel number (mono). */
  num: string
  /** Kicker `Place {i} of {n}` (single place: `Place`) — canon PLACE (R7). */
  personLabel: string
  isMine: boolean
  /** Holder line — name/email; empty state `No one assigned yet` (amber). */
  who: string
  /** Optional sub-line (§7.2): invited → `Invite sent{ to name} — waiting for
   *  them to accept`; child/minor variants are V1.1 seams (§12.2). */
  sub: string
  subTone: 'invited' | 'muted' | null
}

export function seatView(t: Ticket, s: Seat): SeatView {
  const filled = seatFilled(s)
  const invited = s.status === 'pending'
  // TODO(V1.1 kids §12.2): the child / minor sub-line variants —
  //   `On your account — you complete their details`,
  //   `Under 18 · you manage everything · waiver to {guardian}`,
  //   `Under 18 · own login ({email}) · you sign and pay` —
  // need the child/guardian record the kids amendment lands; today's holders
  // are self/invited-adult only, so only the invited sub-line can occur live.
  const sub = invited
    ? `Invite sent${s.holder?.name ? ' to ' + s.holder.name : ''} — waiting for them to accept`
    : s.holder?.kind === 'child'
      ? 'On your account — you complete their details'
      : ''
  return {
    seatNo: s.seatNo,
    filled,
    num: String(s.seatNo),
    personLabel: t.seats.length > 1 ? `Place ${s.seatNo} of ${t.seats.length}` : 'Place',
    isMine: !!s.mine,
    who: filled ? holderLabel(s) : 'No one assigned yet',
    sub,
    subTone: sub ? (invited ? 'invited' : 'muted') : null,
  }
}
